// Stage cost recorder (dev): GPU ms per stage render and every main-thread stall (link, compile check, upload, bake, VideoFrame), against perf.md's budgets.
export const GPU_BUDGET_MS = 4
export const STALL_BUDGET_MS = 10
// Stalls under this are counted, not listed.
const STALL_LIST_MS = 1

let rec = null

/** `fn()`, its wall time recorded as a `kind` stall while a recording runs. */
export const timed = (kind, fn) => {
	if (!rec) return fn()
	const t0 = performance.now()
	try {
		return fn()
	} finally {
		stall(kind, t0)
	}
}

const stall = (kind, t0) => {
	if (!rec) return
	const ms = performance.now() - t0
	const k = (rec.kinds[kind] ??= { n: 0, ms: 0, max: 0 })
	k.n++
	k.ms += ms
	k.max = Math.max(k.max, ms)
	if (ms >= STALL_LIST_MS) rec.stalls.push({ kind, t: t0 - rec.t0, ms, p: fadeP() })
}

const fadeP = () => {
	const c = window.transitionManager?.activeChild?.()
	return c?.kind === 'fade' ? c.progress : null
}

const UPLOADS = ['texImage2D', 'texSubImage2D', 'texImage3D', 'texSubImage3D', 'compressedTexImage2D']

/** Wraps the stage renderer and its context until `stopStageTiming`. Returns false when a recording already runs or there is no stage. */
export const startStageTiming = () => {
	const app = window.pixi_app
	if (rec || !app) return false
	const renderer = app.renderer
	const gl = renderer.gl
	const ext = gl.getExtension('EXT_disjoint_timer_query_webgl2')
	const restore = []
	const wrap = (obj, name, make) => {
		const orig = obj[name]
		obj[name] = make(orig)
		restore.push(() => (obj[name] = orig))
	}
	rec = { t0: performance.now(), frames: [], stalls: [], kinds: {}, links: 0, ext, gl, restore, open: false }
	const r = rec
	const resolve = () => {
		for (const f of r.frames) {
			if (!f.q || !gl.getQueryParameter(f.q, gl.QUERY_RESULT_AVAILABLE)) continue
			if (!gl.getParameter(ext.GPU_DISJOINT_EXT)) f.gpu = gl.getQueryParameter(f.q, gl.QUERY_RESULT) / 1e6
			gl.deleteQuery(f.q)
			f.q = null
		}
	}
	wrap(
		renderer,
		'render',
		orig =>
			function (...args) {
				// One TIME_ELAPSED query at a time: a render nested in a render (a pick, a bake target) is the outer frame's.
				if (r.open) return orig.apply(this, args)
				resolve()
				const q = ext ? gl.createQuery() : null
				if (q) gl.beginQuery(ext.TIME_ELAPSED_EXT, q)
				r.open = true
				const t0 = performance.now()
				try {
					return orig.apply(this, args)
				} finally {
					r.open = false
					if (q) gl.endQuery(ext.TIME_ELAPSED_EXT)
					r.frames.push({ t: t0 - r.t0, cpu: performance.now() - t0, gpu: null, p: fadeP(), q })
				}
			}
	)
	wrap(
		gl,
		'linkProgram',
		orig =>
			function (...args) {
				r.links++
				return orig.apply(this, args)
			}
	)
	wrap(
		gl,
		'getProgramParameter',
		orig =>
			function (prog, pname) {
				if (pname !== gl.LINK_STATUS) return orig.call(this, prog, pname)
				const t0 = performance.now()
				try {
					return orig.call(this, prog, pname)
				} finally {
					stall('link', t0)
				}
			}
	)
	for (const name of UPLOADS)
		wrap(
			gl,
			name,
			orig =>
				function (...args) {
					const t0 = performance.now()
					try {
						return orig.apply(this, args)
					} finally {
						stall('upload', t0)
					}
				}
		)
	if (window.VideoFrame)
		wrap(
			window,
			'VideoFrame',
			VF =>
				new Proxy(VF, {
					construct(target, args) {
						const t0 = performance.now()
						try {
							return Reflect.construct(target, args)
						} finally {
							stall('videoFrame', t0)
						}
					},
				})
		)
	return true
}

// Not rAF: a hidden tab never fires it.
const raf = () => new Promise(res => setTimeout(res, 16))
const r2 = x => Math.round(x * 100) / 100
const stat = xs => {
	if (!xs.length) return null
	const s = [...xs].sort((a, b) => a - b)
	const q = f => s[Math.min(s.length - 1, Math.floor(f * s.length))]
	return { n: s.length, median: r2(q(0.5)), p95: r2(q(0.95)), max: r2(s[s.length - 1]) }
}

/** Ends the recording: GPU, CPU and frame-interval stats, every frame, every stall ≥ 1 ms, per-kind totals and what crossed a budget. */
export const stopStageTiming = async () => {
	if (!rec) return null
	const r = rec
	for (const undo of r.restore.reverse()) undo()
	rec = null
	const { gl } = r
	for (let n = 0; n < 30 && r.frames.some(f => f.q); n++) {
		for (const f of r.frames) {
			if (!f.q || !gl.getQueryParameter(f.q, gl.QUERY_RESULT_AVAILABLE)) continue
			if (!gl.getParameter(r.ext.GPU_DISJOINT_EXT)) f.gpu = gl.getQueryParameter(f.q, gl.QUERY_RESULT) / 1e6
			gl.deleteQuery(f.q)
			f.q = null
		}
		if (r.frames.some(f => f.q)) await raf()
	}
	for (const f of r.frames) if (f.q) gl.deleteQuery(f.q)
	const frames = r.frames.map(f => ({
		t: r2(f.t),
		p: f.p == null ? null : Math.round(f.p * 1e4) / 1e4,
		gpu: f.gpu == null ? null : r2(f.gpu),
		cpu: r2(f.cpu),
	}))
	const gpu = frames.map(f => f.gpu).filter(x => x != null)
	const intervals = frames.slice(1).map((f, i) => f.t - frames[i].t)
	const kinds = Object.fromEntries(
		Object.entries(r.kinds).map(([k, v]) => [k, { n: v.n, ms: r2(v.ms), max: r2(v.max) }])
	)
	return {
		seconds: r2((performance.now() - r.t0) / 1000),
		timer: !!r.ext,
		gpu: stat(gpu),
		cpu: stat(frames.map(f => f.cpu)),
		interval: stat(intervals),
		links: r.links,
		kinds,
		over: {
			gpu: frames.filter(f => f.gpu > GPU_BUDGET_MS).map(f => ({ t: f.t, p: f.p, gpu: f.gpu })),
			stalls: r.stalls.filter(s => s.ms > STALL_BUDGET_MS).map(s => ({ ...s, t: r2(s.t), ms: r2(s.ms) })),
		},
		stalls: r.stalls.map(s => ({ ...s, t: r2(s.t), ms: r2(s.ms) })),
		frames,
	}
}

/** Records `seconds` of the running stage. */
export const recordStageTiming = async (seconds = 5) => {
	if (!startStageTiming()) return null
	await new Promise(res => setTimeout(res, seconds * 1000))
	return stopStageTiming()
}
